import type { Instance } from './catalog'
import { capacityOf, nodeProfile, type ProviderOptions, type Resources } from './providers'

/**
 * How many copies of one pod fit on a node, and what that costs.
 *
 * Allocatable is what Kubernetes itself reports: capacity minus the reservations
 * and the eviction threshold. DaemonSets then run on every node before any of your
 * pods, so they come off the top too. The scheduler places pods by their
 * *requests*, so requests decide the count; limits only decide how far the node is
 * overcommitted if every pod bursts at once.
 */

export interface Workload {
  readonly request: Resources
  /** `null` for a resource with no limit set. */
  readonly limit: { readonly cpu: number | null; readonly memory: number | null }
  readonly daemonsets: Resources & {
    /** DaemonSet pods per node: each one takes a pod slot from the max-pods limit. */
    readonly pods: number
  }
}

/** What stops more pods from fitting. */
export type Constraint = 'cpu' | 'memory' | 'pods'

/** One resource on the node, split the way the bars draw it. All in the resource's unit. */
export interface Usage {
  readonly capacity: number
  readonly reserved: number
  readonly eviction: number
  readonly daemonsets: number
  readonly pods: number
  readonly unused: number
}

export interface NodeFit {
  readonly instance: Instance
  readonly cpu: Usage
  readonly memory: Usage
  /** The provider's pod limit, DaemonSet pods included. */
  readonly maxPods: number
  /** Pods of this size that the node's free CPU and memory alone could hold. */
  readonly podsByResources: number
  /** Pods that actually fit: the smaller of the above and the slots DaemonSets leave. */
  readonly pods: number
  readonly limitedBy: Constraint
  /** Share of the CPU and memory left for your pods that their requests use. 0–1. */
  readonly efficiency: Resources
  /** Pod limits over the same capacity; above 1 means the node is overcommitted. */
  readonly overcommit: { readonly cpu: number | null; readonly memory: number | null }
  /** USD per hour; `Infinity` when not even one pod fits. */
  readonly costPerPod: number
  /** Where the instance price goes, as shares of it that sum to 1. */
  readonly costShares: CostShares
}

export interface CostShares {
  readonly pods: number
  readonly daemonsets: number
  /** Kubernetes and OS reservations plus the eviction threshold. */
  readonly system: number
  readonly unused: number
}

/** Free capacity divided by a request; a zero request never limits. */
const ratio = (free: number, request: number): number =>
  request > 0 ? Math.max(0, free) / request : Infinity

function usage(
  capacity: number,
  reserved: number,
  eviction: number,
  daemonsets: number,
  pods: number,
): Usage {
  return {
    capacity,
    reserved,
    eviction,
    daemonsets,
    pods,
    unused: Math.max(0, capacity - reserved - eviction - daemonsets - pods),
  }
}

/** Free share of a resource, kept in 0–1 so a tiny node cannot produce negative cost. */
const share = (amount: number, capacity: number): number =>
  capacity > 0 ? Math.min(1, Math.max(0, amount / capacity)) : 0

export function fitNode(instance: Instance, options: ProviderOptions, workload: Workload): NodeFit {
  const profile = nodeProfile(instance, options)
  const capacity = capacityOf(instance)
  const { request, daemonsets } = workload

  const freeCpu = capacity.cpu - profile.reserved.cpu - daemonsets.cpu
  const freeMemory =
    capacity.memory - profile.reserved.memory - profile.evictionMemory - daemonsets.memory
  const bySlot = Math.max(0, profile.maxPods - daemonsets.pods)

  const byCpu = ratio(freeCpu, request.cpu)
  const byMemory = ratio(freeMemory, request.memory)
  const byResources = Math.min(byCpu, byMemory)
  const podsByResources = Number.isFinite(byResources) ? Math.floor(byResources) : bySlot
  const pods = Math.min(podsByResources, bySlot)

  let limitedBy: Constraint = byCpu <= byMemory ? 'cpu' : 'memory'
  if (bySlot <= podsByResources) limitedBy = 'pods'

  const cpu = usage(capacity.cpu, profile.reserved.cpu, 0, daemonsets.cpu, pods * request.cpu)
  const memory = usage(
    capacity.memory,
    profile.reserved.memory,
    profile.evictionMemory,
    daemonsets.memory,
    pods * request.memory,
  )

  const efficiency = {
    cpu: freeCpu > 0 ? (pods * request.cpu) / freeCpu : 0,
    memory: freeMemory > 0 ? (pods * request.memory) / freeMemory : 0,
  }
  const overcommitOf = (limit: number | null, free: number): number | null =>
    limit === null || free <= 0 ? null : (pods * limit) / free

  const both = (pick: (u: Usage) => number): number =>
    (share(pick(cpu), cpu.capacity) + share(pick(memory), memory.capacity)) / 2
  const podsShare = both((u) => u.pods)
  const daemonsetsShare = both((u) => u.daemonsets)
  const systemShare = both((u) => u.reserved + u.eviction)

  return {
    instance,
    cpu,
    memory,
    maxPods: profile.maxPods,
    podsByResources,
    pods,
    limitedBy,
    efficiency,
    overcommit: {
      cpu: overcommitOf(workload.limit.cpu, freeCpu),
      memory: overcommitOf(workload.limit.memory, freeMemory),
    },
    costPerPod: pods > 0 ? instance.hourlyPrice / pods : Infinity,
    costShares: {
      pods: podsShare,
      daemonsets: daemonsetsShare,
      system: systemShare,
      unused: Math.max(0, 1 - podsShare - daemonsetsShare - systemShare),
    },
  }
}

/** −90% … +90% in steps of 10: how wrong the requests might be. */
export const SENSITIVITY_STEPS = [-9, -8, -7, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9]

export interface SensitivityPoint {
  /** Tenths: −3 means the pods really use 30% less than they request. */
  readonly step: number
  readonly pods: number
  readonly costPerPod: number
}

/**
 * The cost per pod if the pods' real usage differs from their requests and the
 * requests were corrected to match. Fewer, bigger pods per node when usage is
 * higher; more, smaller ones when it is lower — until the max-pods limit stops the
 * gain, which is exactly what a linear guess would miss.
 */
export function costSensitivity(
  instance: Instance,
  options: ProviderOptions,
  workload: Workload,
): SensitivityPoint[] {
  return SENSITIVITY_STEPS.map((step) => {
    const factor = 1 + step / 10
    const fit = fitNode(instance, options, {
      ...workload,
      request: { cpu: workload.request.cpu * factor, memory: workload.request.memory * factor },
    })
    return { step, pods: fit.pods, costPerPod: fit.costPerPod }
  })
}
